/**
 * <HostNotesEditor> - Iter 9.40 Pass C
 *
 * Admin/host surface for managing Pre/During/Post notes.
 * Endpoints: GET /api/host/notes, POST /api/host/notes, DELETE /api/host/notes/{id}.
 *
 * Triggers (per spec § 9):
 *   - stage:           "pre" | "during" | "post"
 *   - trigger_field_keys[] - only show note if any of these client pref keys are set
 *   - trigger_service_ids[] - only show if these services were booked
 *   - always_show:     bypass triggers
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGES = ["pre", "during", "post"];

export const HostNotesEditor = ({ hostUserId, categoryId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    title: "",
    body: "",
    stage: "pre",
    category_id: categoryId,
    trigger_field_keys: "",
    always_show: true,
    image_url: "",
  });

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (hostUserId) params.append("host_user_id", hostUserId);
      if (categoryId) params.append("category_id", categoryId);
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/host/notes?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotes(data.notes || []);
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    fetchNotes();
  }, [hostUserId, categoryId]); // eslint-disable-line

  const create = async () => {
    if (!draft.title || !draft.body) {
      toast.error("Title and body required");
      return;
    }
    setCreating(true);
    try {
      const token = localStorage.getItem("session_token");
      const body = {
        ...draft,
        trigger_field_keys: draft.trigger_field_keys
          .split(",").map((s) => s.trim()).filter(Boolean),
      };
      const res = await fetch(`${API}/host/notes`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success("Note created");
        setDraft({ ...draft, title: "", body: "", trigger_field_keys: "", image_url: "" });
        await fetchNotes();
      } else {
        toast.error("Failed to create note");
      }
    } catch { toast.error("Failed"); }
    setCreating(false);
  };

  const remove = async (note_id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/host/notes/${note_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Deleted");
        await fetchNotes();
      }
    } catch { toast.error("Failed"); }
  };

  const groupedByStage = STAGES.map((stage) => ({
    stage,
    notes: notes.filter((n) => n.stage === stage),
  }));

  return (
    <div className="space-y-6" data-testid="host-notes-editor">
      <div>
        <h3 className="text-base font-semibold">Host Notes (Pre / During / Post)</h3>
        <p className="text-xs text-gray-500">Surface guidance to clients at the right stage of their experience.</p>
      </div>

      {/* Existing notes by stage */}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : (
        <div className="space-y-4">
          {groupedByStage.map(({ stage, notes }) => (
            <div key={stage}>
              <h4 className="text-xs font-bold uppercase text-gray-500 mb-2">{stage}</h4>
              {notes.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">No {stage} notes yet.</p>
              ) : (
                <div className="space-y-2">
                  {notes.map((n) => (
                    <Card key={n.note_id} data-testid={`host-note-row-${n.note_id}`}>
                      <CardContent className="p-3 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{n.title}</div>
                          <p className="text-xs text-gray-600 whitespace-pre-wrap line-clamp-2">{n.body}</p>
                          {(n.trigger_field_keys || []).length > 0 && (
                            <p className="text-[10px] text-gray-400 mt-1">
                              Triggers on: {(n.trigger_field_keys || []).join(", ")}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => remove(n.note_id)}
                          className="text-gray-400 hover:text-red-500"
                          data-testid={`host-note-delete-${n.note_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create new */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Stage</Label>
              <select
                value={draft.stage}
                onChange={(e) => setDraft({ ...draft, stage: e.target.value })}
                className="w-full mt-1 text-sm rounded-md border border-gray-300 px-2 py-1.5"
                data-testid="host-note-draft-stage"
              >
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Always show</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Switch
                  checked={draft.always_show}
                  onCheckedChange={(v) => setDraft({ ...draft, always_show: v })}
                  data-testid="host-note-draft-always"
                />
                <span className="text-xs text-gray-500">{draft.always_show ? "Always" : "Only when triggered"}</span>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="e.g. Bring a clean face"
              className="mt-1"
              data-testid="host-note-draft-title"
            />
          </div>
          <div>
            <Label className="text-xs">Body</Label>
            <Textarea
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder="Detailed instructions for the client…"
              className="mt-1 min-h-[80px]"
              data-testid="host-note-draft-body"
            />
          </div>
          <div>
            <Label className="text-xs">Trigger field keys (comma-separated, optional)</Label>
            <Input
              value={draft.trigger_field_keys}
              onChange={(e) => setDraft({ ...draft, trigger_field_keys: e.target.value })}
              placeholder="foundation_shade, skin_type"
              className="mt-1"
              data-testid="host-note-draft-triggers"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">Note shows only if client filled any of these preference keys.</p>
          </div>
          <div>
            <Label className="text-xs">Image URL (optional)</Label>
            <Input
              value={draft.image_url}
              onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
              placeholder="https://…"
              className="mt-1"
              data-testid="host-note-draft-image"
            />
          </div>
          <Button onClick={create} disabled={creating} size="sm" data-testid="host-note-create">
            <Plus className="w-4 h-4 mr-1" />
            {creating ? "Creating…" : "Add note"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default HostNotesEditor;
